import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import CustomButton from "../pages/CustomButton";
import DCRPreviewTable from "../Admin/DailyPunchSheets/DcrPreviewTable";
import ReactDOM from "react-dom/client";
import { MemoryRouter } from "react-router-dom";
import formatDate from "../utils/formatDate";

const persistentState = {
  states: [],
  divisions: [],
  employees: [],
  selectedState: "",
  selectedDivision: "All",
  selectedEmployee: "All",
  selectedMonth: new Date().getMonth() + 1,
  selectedYear: new Date().getFullYear(),
  tableData: [],
  showContainers: false,
  searchQuery: "",
  isInitialized: false,
};

const PobDatewise = () => {
  const [states, setStates] = useState(persistentState.states);
  const [divisions, setDivisions] = useState(persistentState.divisions);
  const [employees, setEmployees] = useState(persistentState.employees);
  const [selectedState, setSelectedState] = useState(
    persistentState.selectedState,
  );
  const [selectedDivision, setSelectedDivision] = useState(
    persistentState.selectedDivision,
  );
  const [selectedEmployee, setSelectedEmployee] = useState(
    persistentState.selectedEmployee,
  );
  const [selectedMonth, setSelectedMonth] = useState(
    persistentState.selectedMonth,
  );
  const [selectedYear, setSelectedYear] = useState(
    persistentState.selectedYear,
  );
  const [tableData, setTableData] = useState(persistentState.tableData);
  const [showContainers, setShowContainers] = useState(
    persistentState.showContainers,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [daysInMonth, setDaysInMonth] = useState(31);
  const [searchQuery, setSearchQuery] = useState(persistentState.searchQuery);

  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];
  const currentYear = new Date().getFullYear();

  const yearOptions = Array.from(
    { length: currentYear - 2018 + 1 },
    (_, i) => 2018 + i
  );

  useEffect(() => {
    persistentState.states = states;
    persistentState.divisions = divisions;
    persistentState.employees = employees;
    persistentState.selectedState = selectedState;
    persistentState.selectedDivision = selectedDivision;
    persistentState.selectedEmployee = selectedEmployee;
    persistentState.selectedMonth = selectedMonth;
    persistentState.selectedYear = selectedYear;
    persistentState.tableData = tableData;
    persistentState.showContainers = showContainers;
    persistentState.searchQuery = searchQuery;
  }, [
    states,
    divisions,
    employees,
    selectedState,
    selectedDivision,
    selectedEmployee,
    selectedMonth,
    selectedYear,
    tableData,
    showContainers,
    searchQuery,
  ]);

  // Calculate days in month
  const calculateDaysInMonth = useCallback(() => {
    const days = new Date(selectedYear, selectedMonth, 0).getDate();
    setDaysInMonth(days);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    calculateDaysInMonth();
  }, [calculateDaysInMonth]);
  useEffect(() => {
    const fetchStates = async () => {
      if (!persistentState.isInitialized) {
        try {
          const response = await axios.get(`${BASE_URL}/api/states`, {
            headers: getAuthHeaders(),
          });
          const statesData = response.data || [];
          setStates(statesData);
          persistentState.states = statesData;
          persistentState.isInitialized = true;
        } catch (err) {
          setError("Failed to load states");
        }
      } else {
        setStates(persistentState.states);
        setDivisions(persistentState.divisions);
        setEmployees(persistentState.employees);
        setSelectedState(persistentState.selectedState);
        setSelectedDivision(persistentState.selectedDivision);
        setSelectedEmployee(persistentState.selectedEmployee);
        setSelectedMonth(persistentState.selectedMonth);
        setSelectedYear(persistentState.selectedYear);
        setTableData(persistentState.tableData);
        setShowContainers(persistentState.showContainers);
        setSearchQuery(persistentState.searchQuery);
      }
    };
    fetchStates();
  }, []);
  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    if (!stateId) {
      const emptyEmployees = [{ id: "All", first_name: "All", last_name: "" }];
      setEmployees(emptyEmployees);
      persistentState.employees = emptyEmployees;
      return;
    }

    try {
      let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
      if (divisionName && divisionName !== "All") {
        url += `&division=${encodeURIComponent(divisionName)}`;
      }

      const res = await axios.get(url, { headers: getAuthHeaders() });
      const employeesData = [
        { id: "All", first_name: "All", last_name: "" },
        ...(res.data || []),
      ];
      setEmployees(employeesData);
      persistentState.employees = employeesData;
    } catch (err) {
      const emptyEmployees = [{ id: "All", first_name: "All", last_name: "" }];
      setEmployees(emptyEmployees);
      persistentState.employees = emptyEmployees;
    }
  }, []);

  // Handle state change
  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setSearchQuery("");
    persistentState.selectedState = stateId;
    persistentState.selectedDivision = "All";
    persistentState.selectedEmployee = "All";
    persistentState.searchQuery = "";

    if (!stateId) {
      setDivisions([]);
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      persistentState.divisions = [];
      persistentState.employees = [
        { id: "All", first_name: "All", last_name: "" },
      ];
      return;
    }

    try {
      const divisionRes = await axios.get(
        `${BASE_URL}/api/divisions?state_id=${stateId}`,
        { headers: getAuthHeaders() },
      );
      const divisionsData = divisionRes.data || [];
      setDivisions(divisionsData);
      persistentState.divisions = divisionsData;
    } catch (err) {
      setDivisions([]);
      persistentState.divisions = [];
    }

    fetchEmployees(stateId, "All");
  };

  // Handle division change
  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setSearchQuery("");
    persistentState.selectedDivision = divisionId;
    persistentState.selectedEmployee = "All";
    persistentState.searchQuery = "";

    const division = divisions.find((d) => String(d.id) === String(divisionId));
    const divisionName = division?.division_name || division?.name || "All";

    fetchEmployees(selectedState, divisionName);
  };

  const handleGoClick = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();

      if (selectedState) params.append("state_id", selectedState);
      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find(
          (d) => String(d.id) === String(selectedDivision),
        );
        if (division?.division_name || division?.name) {
          params.append("division", division.division_name || division.name);
        }
      }
      if (selectedEmployee && selectedEmployee !== "All") {
        params.append("employee_id", selectedEmployee);
      }
      if (selectedMonth) params.append("month", selectedMonth);
      if (selectedYear) params.append("year", selectedYear);
      if (searchQuery) params.append("search", searchQuery.trim());

      const url = `${BASE_URL}/api/pob-targets/employee-datewise-visit-pob?${params.toString()}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });

      const data = response.data.data || response.data;
      if (!Array.isArray(data)) {
        throw new Error("Invalid data format received from API");
      }

      setTableData(data);
      setShowContainers(true);
      persistentState.tableData = data;
      persistentState.showContainers = true;
    } catch (err) {
      setError(err.message || "Failed to load data");
      setTableData([]);
      persistentState.tableData = [];
    } finally {
      setIsLoading(false);
    }
  };
  const renderDayHeaders = () => {
    return Array.from({ length: daysInMonth }, (_, day) => {
      const dayNumber = day + 1;
      const dayLetter = tableData[0]?.[`day_${dayNumber}_name`] || "";

      return (
        <th
          key={`day-${dayNumber}`}
          className={`min-w-[40px] text-center align-middle px-2 py-1 ${dayLetter === "S" ? "bg-green-200" : "bg-[#CCFFFF]"
            }`}
        >
          <div className="font-semibold text-sm leading-tight">{dayNumber}</div>
          <div className="text-sm font-semibold mt-0.5">{dayLetter}</div>
        </th>
      );
    });
  };
  const copyStylesToWindow = (targetWindow) => {
    try {
      const doc = targetWindow.document;
      Array.from(document.querySelectorAll("link[rel='stylesheet']")).forEach(
        (link) => {
          const newLink = doc.createElement("link");
          newLink.rel = "stylesheet";
          newLink.href = link.href;
          doc.head.appendChild(newLink);
        },
      );
      Array.from(document.querySelectorAll("style")).forEach((styleTag) => {
        const newStyle = doc.createElement("style");
        newStyle.type = "text/css";
        newStyle.appendChild(doc.createTextNode(styleTag.innerHTML));
        doc.head.appendChild(newStyle);
      });
    } catch (err) {
    }
  };
  const openPreviewInWindow = (payload) => {
    const width = Math.min(window.screen.width - 200, 1200);
    const height = Math.min(window.screen.height - 120, 900);
    const left = Math.max(50, (window.screen.width - width) / 2);
    const top = Math.max(50, (window.screen.height - height) / 4);
    const winName = `dcr_preview_${Date.now()}_${Math.floor(
      Math.random() * 10000,
    )}`;
    const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;

    const newWin = window.open("", winName, features);

    if (!newWin) {
      toast.warning(
        "Popup blocked. Please allow popups to view POB details"
      );
      return;
    }
    newWin.document.title = `POB Preview - ${payload.employeeName || payload.employeeId || ""
      } - ${payload.date || ""}`;
    newWin.document.body.innerHTML = `<div id="dcr-preview-root"></div>`;
    copyStylesToWindow(newWin);
    const container = newWin.document.getElementById("dcr-preview-root");
    const root = ReactDOM.createRoot(container);
    const qs = new URLSearchParams();
    if (payload.employeeId) qs.set("employee_id", payload.employeeId);
    if (payload.date) qs.set("date", payload.date);
    if (payload.stateId) qs.set("state_id", payload.stateId);
    if (payload.divisionId) qs.set("division_id", payload.divisionId);
    const urlPath = `/preview?${qs.toString()}`;
    root.render(
      <React.StrictMode>
        <MemoryRouter initialEntries={[urlPath]}>
          <DCRPreviewTable
            employeeId={payload.employeeId}
            date={payload.date}
            stateId={payload.stateId}
            divisionId={payload.divisionId}
            employeeName={payload.employeeName}
            rowData={payload.rowData}
            cellValue={payload.cellValue}
            onClose={() => {
              try {
                root.unmount();
              } catch (e) { }
              try {
                newWin.close();
              } catch (e) { }
            }}
          />
        </MemoryRouter>
      </React.StrictMode>,
    );
    const cleanup = () => {
      try {
        root.unmount();
      } catch (e) { }
      try {
        newWin.removeEventListener("beforeunload", cleanup);
      } catch (e) { }
    };

    newWin.addEventListener("beforeunload", cleanup);
    return { win: newWin, root };
  };
  const handleDayClick = (employeeId, dateStr, value) => {
    if (!employeeId || !dateStr) return;

    openPreviewInWindow({
      employeeId,
      date: dateStr,
      stateId: selectedState,
      divisionId: selectedDivision,
      employeeName: null,
      rowData: null,
      cellValue: value,
    });
  };

  const renderDayCells = (row) => {
    return Array.from({ length: daysInMonth }, (_, day) => {
      const dayNumber = day + 1;
      const value = row[`day_${dayNumber}`];
      const dayLetter = row[`day_${dayNumber}_name`] || "";

      const isClickable =
        value !== "-" && value !== null && value !== undefined;

      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(
        2,
        "0",
      )}-${String(dayNumber).padStart(2, "0")}`;

      return (
        <td
          key={`day-${dayNumber}-${row.employee_id}`}
          className={`px-1 py-1 text-center align-middle ${dayLetter === "S" ? "bg-red-100" : ""
            } ${isClickable ? "cursor-pointer text-blue-600 underline" : ""}`}
          style={{ fontSize: "12px" }}
          onClick={() => {
            if (!isClickable) return;

            handleDayClick(
              row.employee_id,
              dateStr, // ✅ CORRECT DATE
              value,
            );
          }}
          title={isClickable ? "Click to open DCR details" : ""}
        >
          {value}
        </td>
      );
    });
  };
  const uniqueEmployees = Array.from(
    new Map(
      tableData
        .filter(emp => emp && emp.employee_id)
        .map(emp => [emp.employee_id, emp])
    ).values()
  );
  const exportToExcel = async () => {
    if (uniqueEmployees.length === 0) {
      toast.info("No data to export");
      return;
    }

    try {
      setIsLoading(true);

      const wb = XLSX.utils.book_new();
      const ws = {};

      // =====================================
      // HEADERS
      // =====================================
      const headers = [
        "S.No",
        "Emp ID",
        "State",
        "Employee",
        "Designation",
        "City/HQ",
        "Division",
        "DOJ",
        "DOR",
        "Manager",
        "Total",
        ...Array.from(
          { length: daysInMonth },
          (_, i) => `${i + 1}`
        ),
      ];

      // =====================================
      // STYLES
      // =====================================
      const headerStyle = {
        font: {
          bold: true,
          color: { rgb: "FFFFFF" },
          sz: 11,
        },
        fill: {
          fgColor: { rgb: "2563EB" },
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
        border: {
          top: { style: "thin", color: { rgb: "D1D5DB" } },
          bottom: { style: "thin", color: { rgb: "D1D5DB" } },
          left: { style: "thin", color: { rgb: "D1D5DB" } },
          right: { style: "thin", color: { rgb: "D1D5DB" } },
        },
      };

      const rowStyle = {
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
        border: {
          top: { style: "thin", color: { rgb: "E5E7EB" } },
          bottom: { style: "thin", color: { rgb: "E5E7EB" } },
          left: { style: "thin", color: { rgb: "E5E7EB" } },
          right: { style: "thin", color: { rgb: "E5E7EB" } },
        },
      };

      // =====================================
      // ADD HEADERS
      // =====================================
      headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({
          r: 0,
          c: colIndex,
        });

        ws[cellAddress] = {
          v: header,
          t: "s",
          s: headerStyle,
        };
      });

      // =====================================
      // ADD ROWS
      // =====================================
      // =====================================
      // ADD ROWS
      // =====================================
      uniqueEmployees.forEach((row, index) => {
        const excelRow = index + 2;

        const empRaw = String(row.employee ?? "").trim();
        const empIsNum = /^\d+$/.test(empRaw);
        const empID = empIsNum ? Number(empRaw) : empRaw;
const employeeName =
  row.status === "RESIGNED"
    ? `${row.employee_name || ""} (RESIGNED)`
    : row.employee_name || "";
        const rowData = [
          index + 1,
          empID,
          row.state || "",
          employeeName,    
          row.designation || "",
          row.city_hq || "",
          row.division || "",
          row.final_joining_date || "",  
          row.resign_date || "",         
          row.manager || "",
          Number(row.total || 0),
        ];

        // Dynamic Days
        for (let day = 1; day <= daysInMonth; day++) {
          rowData.push(row[`day_${day}`] ?? "-");
        }

        rowData.forEach((value, colIndex) => {
          const cellAddress = XLSX.utils.encode_cell({ r: index + 1, c: colIndex });

          // DOJ = col 7 (index 7), DOR = col 8 (index 8)
          if (colIndex === 7 || colIndex === 8) {
            const dateVal = value;
            if (dateVal) {
              try {
                const date = new Date(
                  /^\d{4}-\d{2}-\d{2}$/.test(String(dateVal))
                    ? `${dateVal}T00:00:00`
                    : dateVal
                );
                if (!isNaN(date.getTime())) {
                  ws[cellAddress] = {
                    v: date,           // ✅ Real Excel Date object
                    t: "d",            // ✅ Date type
                    z: "dd-mmm-yyyy",  // ✅ Excel date format
                    s: rowStyle,
                  };
                  return; // skip the generic handler below
                }
              } catch { }
            }
            // Fallback: empty cell
            ws[cellAddress] = { v: "", t: "s", s: rowStyle };
            return;
          }

          ws[cellAddress] = {
            v: value,
            t:
              colIndex === 1
                ? empIsNum ? "n" : "s"
                : typeof value === "number" ? "n" : "s",
            s: rowStyle,
          };
        });


      });

      // =====================================
      // SHEET RANGE
      // =====================================
      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: {
          r: uniqueEmployees.length,
          c: headers.length - 1,
        },
      });

      // =====================================
      // COLUMN WIDTHS
      // =====================================
      ws["!cols"] = [
        { wch: 8 },
        { wch: 12 },
        { wch: 15 },
        { wch: 30 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
        { wch: 25 },
        { wch: 10 },
        ...Array.from(
          { length: daysInMonth },
          () => ({ wch: 6 })
        ),
      ];

      // =====================================
      // FREEZE HEADER
      // =====================================
      ws["!freeze"] = {
        xSplit: 0,
        ySplit: 1,
      };

      // =====================================
      // FILTERS
      // =====================================
      ws["!autofilter"] = {
        ref: `A1:${XLSX.utils.encode_col(
          headers.length - 1
        )}1`,
      };

      // =====================================
      // APPEND SHEET
      // =====================================
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        "POB Datewise"
      );

      // =====================================
      // FILE NAME
      // =====================================
      const monthName =
        monthOptions.find(
          (m) => m.value === selectedMonth
        )?.label || selectedMonth;

      const fileName = `POB_Datewise_${monthName}_${selectedYear}.xlsx`;

      // =====================================
      // DOWNLOAD
      // =====================================
      XLSX.writeFile(wb, fileName, {
        compression: true,
      });

    } catch (err) {
      toast.error("Failed to export data");
    } finally {
      setIsLoading(false);
    }
  };
  const handlePrint = () => {
    if (uniqueEmployees.length === 0) {
      toast.info("No data to print");
      return;
    }

    try {
      const printWindow = window.open(
        "",
        "_blank",
        "width=1200,height=800,scrollbars=yes",
      );
      if (!printWindow) {
        toast.warning("Please allow popups to print");
        return;
      }
      const tableWrapper = document.querySelector(".table-wrapper");
      const tableHtml = tableWrapper ? tableWrapper.innerHTML : "";

      const monthName =
        monthOptions.find((m) => m.value === selectedMonth)?.label || "";
      const stateName =
        states.find((s) => String(s.id) === String(selectedState))?.name ||
        "All";
      const divisionName =
        selectedDivision === "All"
          ? "All"
          : divisions.find((d) => String(d.id) === String(selectedDivision))
            ?.division_name ||
          divisions.find((d) => String(d.id) === String(selectedDivision))
            ?.name ||
          "N/A";
      const printStyles = `
        @page { size: landscape; margin: 10mm; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 16px; color: #111; font-size: 10px; }
        .print-wrapper { width: 100%; }
        .print-header { text-align: center; margin-bottom: 12px; }
        .print-title { font-size: 18px; font-weight: bold; color: #1f2937; }
        .print-filters { font-size: 12px; margin-top: 6px; color: #374151; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 9px; }
        th, td { border: 1px solid #ddd; padding: 6px; text-align: center; white-space: nowrap; }
        thead th { background-color: #0ea5e9; color: #ffffff; font-weight: 600; }
        .sunday-header { background-color: #ffe6e6; }
        .day-number { font-weight: bold; font-size: 10px; }
        .day-letter { font-size: 8px; }
        tr { page-break-inside: avoid; }
        @media print {
          body { padding: 6px; }
          .print-title { font-size: 16px; }
        }
      `;
      const printHtml = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>POB Datewise Report</title>
            <style>${printStyles}</style>
          </head>
          <body>
            <div class="print-wrapper">
              <div class="print-header">
                <div class="print-title">POB Datewise Report</div>
                <div class="print-filters">
                  <div><strong>State:</strong> ${stateName}</div>
                  <div><strong>Division:</strong> ${divisionName}</div>
                  <div><strong>Period:</strong> ${monthName} ${selectedYear}</div>
                  ${searchQuery
          ? `<div><strong>Search:</strong> ${searchQuery}</div>`
          : ""
        }
                  <div><strong>Total Records:</strong> ${uniqueEmployees.length
        }</div>
                  <div><strong>Generated on:</strong> ${new Date().toLocaleString(
          "en-GB",
        )}</div>
                </div>
              </div>
              ${tableHtml}
            </div>
            <script>
              window.onload = function() {
                setTimeout(function(){ window.print(); }, 300);
              }
            </script>
          </body>
        </html>
      `;
      printWindow.document.open();
      printWindow.document.write(printHtml);
      printWindow.document.close();
      try {
        copyStylesToWindow(printWindow);
      } catch (e) { }
    } catch (error) {
      setError("Failed to generate print view");
      toast.error("Failed to generate print view");
    }
  };

  return (
    <div className="w-full p-2 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">POB </span>
          <span className="text-blue-500">Datewise</span>
        </h1>

        <div className="grid grid-cols-5 gap-2 items-center text-sm font-sans mb-2">
          <label className="text-right whitespace-nowrap">State</label>
          <select
            className="w-full p-2 border rounded"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            <option value="">All</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="text-right whitespace-nowrap">Division</label>
          <select
            className="w-full p-2 border rounded"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={isLoading || !selectedState}
          >
            <option value="All">All</option>
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.division_name || division.name}
              </option>
            ))}
          </select>
          <div />
        </div>

        <div className="grid grid-cols-5 gap-2 items-center text-sm font-sans mt-2">
          <label className="text-right whitespace-nowrap">Year</label>
          <select
            className="w-full p-2 border rounded"
            value={selectedYear}
            onChange={(e) => {
              const year = parseInt(e.target.value, 10);
              setSelectedYear(year);
              persistentState.selectedYear = year;
            }}
            disabled={isLoading}
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <label className="text-right whitespace-nowrap">Month</label>
          <select
            className="w-full p-2 border rounded"
            value={selectedMonth}
            onChange={(e) => {
              const month = parseInt(e.target.value, 10);
              setSelectedMonth(month);
              persistentState.selectedMonth = month;
            }}
            disabled={isLoading}
          >
            {monthOptions.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>

          <div />
          <div />
        </div>

        <div className="flex justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>

        {error && <div className="text-red-600 mt-3">{error}</div>}
      </div>

      {showContainers && (
        <>
          <div className="border rounded shadow  table-wrapper">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "50px" }}
                    className="px-2 py-2"
                  >
                    S.No
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "80px" }}
                    className="px-2 py-2"
                  >
                    Emp ID
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "120px" }}
                    className="px-2 py-2"
                  >
                    State
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "200px" }}
                    className="px-2 py-2 text-left"
                  >
                    Employee
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "150px" }}
                    className="px-2 py-2"
                  >
                    Designation
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "120px" }}
                    className="px-2 py-2"
                  >
                    City/HQ
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "120px" }}
                    className="px-2 py-2"
                  >
                    Division
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "100px" }}
                    className="px-2 py-2"
                  >
                    DOJ
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "100px" }}
                    className="px-2 py-2"
                  >
                    DOR
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "180px" }}
                    className="px-2 py-2"
                  >
                    Manager
                  </th>
                  <th
                    rowSpan="2"
                    style={{ minWidth: "80px" }}
                    className="px-2 py-2"
                  >
                    Total
                  </th>
                  {renderDayHeaders()}
                </tr>
              </thead>
              <tbody>
                {uniqueEmployees.length > 0 ? (
                  uniqueEmployees.map((row, index) => (
                    <tr
                      key={`${row.employee_id}-${index}`}
                      className="even:bg-white odd:bg-slate-50"
                    >
                      <td
                        style={{ minWidth: "50px" }}
                        className="px-2 py-2 text-center"
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{ minWidth: "80px" }}
                        className="px-2 py-2 text-center"
                      >
                        {row.employee || "-"}
                      </td>
                      <td style={{ minWidth: "120px" }} className="px-2 py-2">
                        {row.state || "-"}
                      </td>
                      <td style={{ minWidth: "200px" }} className="px-2 py-2">
                        {row.employee_name || "N/A"}
                        {row.status === "RESIGNED" && (
                          <span className="text-red-600 font-bold">
                            {" "}
                            ({row.status})
                          </span>
                        )}
                      </td>
                      <td style={{ minWidth: "150px" }} className="px-2 py-2">
                        {row.designation || "-"}
                      </td>
                      <td style={{ minWidth: "120px" }} className="px-2 py-2">
                        {row.city_hq || "-"}
                      </td>
                      <td style={{ minWidth: "120px" }} className="px-2 py-2">
                        {row.division || "-"}
                      </td>
                      <td
                        style={{ minWidth: "100px" }}
                        className="px-2 py-2 text-center"
                      >
                        {formatDate(row.final_joining_date) || "-"}
                      </td>
                      <td
                        style={{ minWidth: "100px" }}
                        className="px-2 py-2 text-center"
                      >
                        {formatDate(row.resign_date) || "-"}
                      </td>
                      <td style={{ minWidth: "180px" }} className="px-2 py-2">
                        {row.manager || "-"}
                      </td>
                      <td
                        style={{ minWidth: "80px" }}
                        className="px-2 py-2 text-center font-semibold"
                      >
                        {row.total || 0}
                      </td>
                      {renderDayCells(row)}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={11 + daysInMonth} className="text-center py-6">
                      No data found for the selected filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {uniqueEmployees.length > 0 && (
            <div className="mt-4 mb-4 flex gap-4 justify-center">
              <CustomButton
                onClick={handlePrint}
                disabled={isLoading}
                label="Print"
                variant="print"
              >
                🖨
              </CustomButton>

              <CustomButton
                onClick={exportToExcel}
                disabled={isLoading}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PobDatewise;
